/**
 * Projects: creating, opening, saving, versioning, deleting, and moving in and out of
 * `.padlab` packages.
 *
 * The rule the whole file is built around is that a user's recording is irreplaceable.
 * Everything else follows from it:
 *
 * - **A save is atomic.** The new document, its metadata, and the pointer that says which
 *   document is current are written in one IndexedDB transaction. A tab killed halfway
 *   through leaves the previous project intact — there is no state in which the pointer
 *   names a document that was never finished.
 * - **The previous document is kept.** Every save leaves the one it replaced in place as a
 *   backup, so a document that somehow fails to parse still has a predecessor to fall back
 *   to, and a snapshot behind that.
 * - **Autosave is debounced but never optional.** Edits coalesce for 1.2 s, and the pair of
 *   `visibilitychange` and `pagehide` guarantees a flush when iOS takes the app away.
 *   `unload` is never used: it is unreliable on iOS and it disables the back-forward cache.
 * - **Version history is real.** Up to twenty snapshots per project, no more often than one
 *   every five minutes, matching the native store exactly.
 *
 * The `.padlab` package written here is byte-compatible with the one the iPhone app writes:
 * a store-only ZIP holding `project.json` and `Media/…`, with real CRC-32s and a real
 * central directory, built by hand because a store-only writer is 200 lines and a dependency
 * in the audio path is not worth avoiding them.
 */

import { signal, type ReadonlySignal } from '../core/signal';
import { createLogger, bytes as byteTag, idPrefix } from '../core/log';
import { decodeProject, encodeProject, isProjectSchemaError } from '../model/schema';
import {
  makeBank,
  makeProject,
  nowISODate,
  padIsEmpty,
  projectAllPads,
  sanitiseProject,
  toISODate,
  newUUID,
  type GridSize,
  type ISODate,
  type PadColor,
  type Project,
  type ProjectSummary,
  type SampleAsset,
  type UUID,
} from '../model/types';
import { getBlobStore, type BlobStore } from './blob-store';
import {
  LIBRARY_ASSET_TAG,
  StorageError,
  artworkKey,
  assetKey,
  mediaKeyForAsset,
  blobCategory,
  keyRange,
  readSetting,
  readStore,
  storageEstimate,
  transact,
  writeSetting,
  writeStore,
  type AssetRecord,
  type ProjectRecord,
  type RevisionRecord,
  type Transaction,
} from './db';

const log = createLogger('projects');

// MARK: - Constants (kept in step with `StoragePaths` on the native side)

/** Soft-deleted projects survive this long before the store reclaims them. */
export const TRASH_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
/** Number of version-history snapshots kept per project. */
export const SNAPSHOT_LIMIT = 20;
/** A snapshot is taken at most this often, so a long session does not fill the disk. */
export const SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Long enough that a slider drag is one write, short enough that "Saved" appears while the
 * user still remembers making the change.
 */
export const AUTOSAVE_DEBOUNCE_MS = 1200;
/** A failed write is usually transient. One quick retry fixes most without nagging. */
export const AUTOSAVE_RETRY_MS = 3000;

export const PACKAGE_EXTENSION = 'padlab';
const PROJECT_ENTRY_NAME = 'project.json';
const MEDIA_ENTRY_PREFIX = 'Media/';
/** Pictures pads wear. A separate namespace so a media entry can never collide with one. */
const ARTWORK_ENTRY_PREFIX = 'Artwork/';

const LAST_OPENED_KEY = 'projects.lastOpened';

// MARK: - Public types

export type AutosaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: ISODate }
  | { kind: 'failed'; message: string };

export function autosaveStatusLabel(status: AutosaveStatus): string {
  switch (status.kind) {
    case 'idle':
      return '';
    case 'pending':
      return 'Unsaved';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'failed':
      return 'Save failed';
  }
}

export interface ProjectSnapshot {
  id: string;
  createdAt: ISODate;
  byteSize: number;
  isAutomatic: boolean;
  label?: string;
}

/** Mirrors the native `ProjectTemplate`, minus the bundled starter file names. */
export interface ProjectTemplate {
  id: string;
  name: string;
  summary: string;
  symbolName: string;
  tint: PadColor;
  grid: GridSize;
  tempo: number;
}

export const BLANK_TEMPLATE: ProjectTemplate = {
  id: 'blank',
  name: 'Blank Board',
  summary: 'An empty grid. Start by recording or importing a sound.',
  symbolName: 'square.grid.3x3',
  tint: 'slate',
  grid: 'g3x4',
  tempo: 96,
};

export interface StorageReport {
  projectsBytes: number;
  mediaBytes: number;
  peaksBytes: number;
  rendersBytes: number;
  recordingsBytes: number;
  downloadedPacksBytes: number;
  /** What the browser says is left. Zero when it declines to say. */
  availableBytes: number;
  totalBytes: number;
  /** Everything regenerable. Clearing this never touches an original recording. */
  reclaimableBytes: number;
}

export interface LoadOutcome {
  project: Project;
  /** Set when the primary document could not be read and something older was opened. */
  recovery?: string;
}

// MARK: - Store

let storePromise: Promise<ProjectStore> | null = null;

/** Opens the project store once and hands the same instance to every caller. */
export function getProjectStore(): Promise<ProjectStore> {
  storePromise ??= (async (): Promise<ProjectStore> => {
    const blobs = await getBlobStore();
    const store = new ProjectStore(blobs);
    await store.refreshSummaries();
    return store;
  })();
  return storePromise;
}

/**
 * A pack is the sounds, not the arrangement.
 *
 * The package format is reused deliberately — the importer already reads it, so there is no
 * second format to maintain — but the document inside carries only the chosen assets and the
 * pads that point at them. Patterns, the song and the scenes are dropped; `sanitiseProject`
 * puts a fresh empty pattern back on import.
 */
/**
 * The tag every generated library sound carries. Duplicated as a literal rather than
 * imported: the storage layer must not depend on the app layer, and this string is part of
 * the on-disk format either way.
 */
const GENERATED_LIBRARY_TAG = 'padlab library';

/**
 * Drops generated library sounds nothing in the project uses.
 *
 * The three-hundred-sound library is written into whichever project is open, so a backup of
 * a sixteen-pad board carried three hundred assets and roughly eighty megabytes of audio the
 * user never chose, never edited and never heard — and which any PadLab regenerates from the
 * same catalogue on the device. What travels is the user's work: everything they recorded or
 * imported, plus every library sound a pad, a track or a scene actually points at.
 */
function withoutUnusedLibrary(project: Project): Project {
  const used = new Set<UUID>();
  for (const bank of project.banks) {
    for (const pad of bank.pads) if (pad.assetID !== undefined) used.add(pad.assetID);
  }
  for (const pattern of project.patterns) {
    for (const track of pattern.tracks) {
      if (track.padID === undefined) continue;
      const pad = project.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === track.padID);
      if (pad?.assetID !== undefined) used.add(pad.assetID);
    }
  }
  const trimmed = project.assets.filter(
    (asset) => used.has(asset.id) || !asset.tags.includes(GENERATED_LIBRARY_TAG),
  );
  if (trimmed.length === project.assets.length) return project;
  const copy = structuredClone(project);
  copy.assets = trimmed;
  return copy;
}

function soundsOnlyProject(project: Project, assetIDs: readonly UUID[]): Project {
  const keep = new Set(assetIDs);
  const pack = structuredClone(project);
  pack.assets = pack.assets.filter((asset) => keep.has(asset.id));
  pack.patterns = [];
  pack.scenes = [];
  pack.song.sections = [];
  for (const bank of pack.banks) {
    for (const pad of bank.pads) {
      if (pad.assetID === undefined || keep.has(pad.assetID)) continue;
      delete pad.assetID;
      pad.name = '';
    }
  }
  return pack;
}

/**
 * True when a record is soft-deleted.
 *
 * `deletedAt` is absent on a live project, but a record that has round-tripped through
 * JSON — a `.padlab` import, a migration, a hand-edited backup — comes back with an
 * explicit `null` instead. A strict `=== undefined` test therefore classified *every*
 * reloaded project as trashed, and the home screen showed "No projects yet" while the
 * database was full. Both spellings mean the same thing and both are checked here.
 */
function isDeleted(value: string | null | undefined): value is string {
  return value !== undefined && value !== null;
}

export class ProjectStore {
  private readonly blobs: BlobStore;

  private readonly summariesSignal = signal<ProjectSummary[]>([]);
  private readonly trashedSignal = signal<ProjectSummary[]>([]);
  private readonly autosaveSignal = signal<AutosaveStatus>({ kind: 'idle' });

  /** Newest project handed to `markDirty`, cleared only by a write that succeeded. */
  private pending: Project | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Head of the write chain: each save awaits the last, so two never overlap. */
  private writeChain: Promise<void> = Promise.resolve();
  private hasRetriedCurrentFailure = false;

  constructor(blobs: BlobStore) {
    this.blobs = blobs;
    this.installLifecycleFlush();
  }

  /** Live list of projects that are not in the trash, newest first. */
  get summaries(): ReadonlySignal<ProjectSummary[]> {
    return this.summariesSignal;
  }

  /** Live list of soft-deleted projects still inside their recovery window. */
  get trashed(): ReadonlySignal<ProjectSummary[]> {
    return this.trashedSignal;
  }

  get autosaveStatus(): ReadonlySignal<AutosaveStatus> {
    return this.autosaveSignal;
  }

  get hasUnsavedChanges(): boolean {
    return this.pending !== null;
  }

  // MARK: Listing

  async loadSummaries(): Promise<ProjectSummary[]> {
    await this.refreshSummaries();
    return this.summariesSignal.peek();
  }

  async refreshSummaries(): Promise<void> {
    const records = await readStore('projects', (store) => store.getAll());
    const live: ProjectSummary[] = [];
    const trashed: ProjectSummary[] = [];
    for (const record of records) {
      const summary = toSummary(record);
      if (!isDeleted(record.deletedAt)) live.push(summary);
      else trashed.push(summary);
    }
    live.sort((left, right) => (left.modifiedAt < right.modifiedAt ? 1 : -1));
    trashed.sort((left, right) => ((left.deletedAt ?? '') < (right.deletedAt ?? '') ? 1 : -1));
    this.summariesSignal.value = live;
    this.trashedSignal.value = trashed;
  }

  /** The project to reopen on launch, so an installed PWA comes back where it was. */
  async lastOpenedProjectID(): Promise<UUID | null> {
    const id = await readSetting<string | null>(LAST_OPENED_KEY, null);
    if (id === null) return null;
    const record = await readStore('projects', (store) => store.get(id));
    return record === undefined || isDeleted(record.deletedAt) ? null : record.id;
  }

  async setLastOpenedProjectID(id: UUID): Promise<void> {
    await writeSetting(LAST_OPENED_KEY, id);
    await writeStore('projects', async (store) => {
      const record = await store.get(id);
      if (record !== undefined) await store.put({ ...record, lastOpenedAt: nowISODate() });
    });
  }

  // MARK: Loading

  async load(id: UUID): Promise<Project> {
    return (await this.loadWithOutcome(id)).project;
  }

  /**
   * Opens a project, falling back through the backup document and then the snapshots when
   * the current one cannot be read. The damaged document is left in place: it is evidence,
   * and a user who lost work deserves better than having it deleted for tidiness.
   */
  async loadWithOutcome(id: UUID): Promise<LoadOutcome> {
    const record = await readStore('projects', (store) => store.get(id));
    if (record === undefined) throw StorageError.notFound('That project');

    const primary = await this.readRevision(record.currentRevision);
    if (primary !== null) return { project: primary };

    if (record.previousRevision !== undefined) {
      const backup = await this.readRevision(record.previousRevision);
      if (backup !== null) {
        log.warn('opened the backup document', { project: idPrefix(id) });
        return {
          project: backup,
          recovery:
            'The project file was damaged, so PadLab opened the backup saved just before your last change. The damaged copy has been kept.',
        };
      }
    }

    const snapshots = await this.snapshots(id);
    for (const snapshot of snapshots) {
      const restored = await this.readRevision(snapshot.id);
      if (restored !== null) {
        log.warn('opened a snapshot', { project: idPrefix(id) });
        return {
          project: restored,
          recovery:
            'The project file and its backup were both damaged, so PadLab opened your most recent snapshot. Anything changed after that snapshot is only in the damaged copy, which has been kept.',
        };
      }
    }

    throw StorageError.corrupt('That project');
  }

  private async readRevision(key: string): Promise<Project | null> {
    const document = await readStore('revisionDocuments', (store) => store.get(key));
    if (document === undefined) return null;
    try {
      return decodeProject(document.text);
    } catch (cause) {
      if (isProjectSchemaError(cause) && cause.kind !== 'corrupt') throw cause;
      log.error('a stored project document could not be decoded', cause, { revision: idPrefix(key) });
      return null;
    }
  }

  // MARK: Saving

  /**
   * Writes the project now and returns when it is genuinely stored.
   *
   * The document, its metadata and the pointer flip share one transaction; the snapshot
   * decision and the pruning of superseded revisions ride along in it, so the twenty-snapshot
   * cap can never be exceeded by two saves racing.
   */
  async save(project: Project, options: { snapshotLabel?: string } = {}): Promise<ProjectSummary> {
    const sanitised = sanitiseProject(project);
    const text = encodeProject(sanitised);
    const byteSize = new Blob([text]).size;
    const now = new Date();
    const createdAt = toISODate(now);

    const summary = await transact(
      ['projects', 'revisions', 'revisionDocuments', 'assets'],
      'readwrite',
      async (tx) => {
        const projects = tx.store('projects');
        const revisions = tx.store('revisions');
        const documents = tx.store('revisionDocuments');

        const existing = await projects.get(sanitised.id);
        const sequence = (existing?.sequence ?? 0) + 1;
        const key = revisionKey(sanitised.id, sequence);

        const explicit = options.snapshotLabel !== undefined;
        const dueForSnapshot =
          existing === undefined ||
          existing.lastSnapshotAt === undefined ||
          now.getTime() - Date.parse(existing.lastSnapshotAt) >= SNAPSHOT_INTERVAL_MS;
        const isSnapshot = explicit || dueForSnapshot;

        await documents.put({ key, text });
        const revision: RevisionRecord = {
          key,
          projectID: sanitised.id,
          sequence,
          createdAt,
          byteSize,
          isSnapshot,
          isAutomatic: !explicit,
        };
        if (options.snapshotLabel !== undefined) revision.label = options.snapshotLabel;
        await revisions.put(revision);

        const record: ProjectRecord = {
          id: sanitised.id,
          sequence,
          currentRevision: key,
          name: sanitised.name,
          createdAt: sanitised.createdAt,
          modifiedAt: sanitised.modifiedAt,
          padCount: projectAllPads(sanitised).filter((pad) => !padIsEmpty(pad)).length,
          assetCount: sanitised.assets.length,
          tempo: sanitised.tempo,
          byteSize,
          isArchived: sanitised.isArchived,
        };
        // The document just replaced becomes the backup; the one *it* replaced is only kept
        // if it was marked as a snapshot.
        if (existing !== undefined) record.previousRevision = existing.currentRevision;
        if (sanitised.artworkFileName !== undefined) record.artworkFileName = sanitised.artworkFileName;
        if (isDeleted(sanitised.deletedAt)) record.deletedAt = sanitised.deletedAt;
        if (existing?.lastOpenedAt !== undefined) record.lastOpenedAt = existing.lastOpenedAt;
        record.lastSnapshotAt = isSnapshot ? createdAt : existing?.lastSnapshotAt;
        if (record.lastSnapshotAt === undefined) delete record.lastSnapshotAt;

        await projects.put(record);

        await pruneRevisions(tx, sanitised.id, record);
        await mirrorAssets(tx, sanitised);

        return toSummary(record);
      },
    );

    // Patching the one row that changed rather than re-reading every project: autosave runs
    // every 1.2 s during an edit, and a full rescan per keystroke is work the user pays for.
    this.applySummary(summary);
    return summary;
  }

  private applySummary(summary: ProjectSummary): void {
    const live = this.summariesSignal.peek().filter((entry) => entry.id !== summary.id);
    const trashed = this.trashedSignal.peek().filter((entry) => entry.id !== summary.id);
    if (!isDeleted(summary.deletedAt)) {
      live.push(summary);
      live.sort((left, right) => (left.modifiedAt < right.modifiedAt ? 1 : -1));
    } else {
      trashed.push(summary);
      trashed.sort((left, right) => ((left.deletedAt ?? '') < (right.deletedAt ?? '') ? 1 : -1));
    }
    this.summariesSignal.value = live;
    this.trashedSignal.value = trashed;
  }

  // MARK: Autosave

  /**
   * Records a change. Cheap by design — it is called as often as a finger moves on a fader —
   * and the coordinator decides when to write.
   */
  markDirty(project: Project): void {
    this.pending = project;
    // A fresh edit earns a fresh retry budget: whatever failed last time may well be gone.
    this.hasRetriedCurrentFailure = false;
    this.clearTimer('retry');
    this.autosaveSignal.value = { kind: 'pending' };

    this.clearTimer('debounce');
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.enqueueWrite();
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  /** Writes now and waits. Called on background transition and before every export. */
  async flush(): Promise<void> {
    this.clearTimer('debounce');
    this.clearTimer('retry');
    await this.enqueueWrite();
  }

  private enqueueWrite(): Promise<void> {
    const next = this.writeChain.then(() => this.writePending());
    this.writeChain = next.catch(() => undefined);
    return next;
  }

  private async writePending(): Promise<void> {
    const project = this.pending;
    if (project === null) return;

    this.autosaveSignal.value = { kind: 'saving' };
    try {
      await this.save(project);
      // Only a write that succeeded clears the pending project; anything else and the next
      // attempt would have nothing to save.
      if (this.pending === project) this.pending = null;
      this.autosaveSignal.value = { kind: 'saved', at: nowISODate() };
    } catch (cause) {
      const message = cause instanceof StorageError ? cause.message : 'That change could not be saved.';
      this.autosaveSignal.value = { kind: 'failed', message };
      log.error('autosave failed', cause, { project: idPrefix(project.id) });
      if (!this.hasRetriedCurrentFailure) {
        this.hasRetriedCurrentFailure = true;
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          void this.enqueueWrite();
        }, AUTOSAVE_RETRY_MS);
      }
    }
  }

  private clearTimer(which: 'debounce' | 'retry'): void {
    const timer = which === 'debounce' ? this.debounceTimer : this.retryTimer;
    if (timer !== null) clearTimeout(timer);
    if (which === 'debounce') this.debounceTimer = null;
    else this.retryTimer = null;
  }

  /**
   * `visibilitychange` is the one iOS actually delivers when the user swipes home or takes a
   * call, and it arrives while the page still has time to finish an IndexedDB write.
   * `pagehide` is the belt to that braces — it fires on a real teardown and on a
   * back-forward-cache suspend, and unlike `unload` it does not disable the cache.
   */
  private installLifecycleFlush(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.pending !== null) void this.flush();
    });
    window.addEventListener('pagehide', () => {
      if (this.pending !== null) void this.flush();
    });
  }

  // MARK: Lifecycle

  async create(name: string, template: ProjectTemplate = BLANK_TEMPLATE): Promise<Project> {
    const trimmed = name.trim();
    const project = makeProject(trimmed === '' ? template.name : trimmed, {
      tempo: template.tempo,
      banks: [makeBank('Bank A', { grid: template.grid, color: template.tint })],
    });
    // A blank board is blank: no audio is synthesised and no file is written. Starter content
    // is imported through the media library after the project exists, so a template that
    // ships sounds and one that does not take the same path.
    for (const pad of project.banks[0]?.pads ?? []) pad.color = template.tint;
    await this.save(project, { snapshotLabel: 'Created' });
    return project;
  }

  async duplicate(id: UUID, newName: string): Promise<Project> {
    const source = await this.load(id);
    const copy = structuredClone(source);
    const newID = newUUID();
    const trimmed = newName.trim();

    copy.id = newID;
    copy.name = trimmed === '' ? `${source.name} Copy` : trimmed;
    copy.createdAt = nowISODate();
    copy.modifiedAt = copy.createdAt;
    copy.isArchived = false;
    delete copy.deletedAt;

    // Media is copied before the project is written: a duplicate whose pads point at sounds
    // that are still being copied would be playable-but-silent for as long as it took.
    const copied: string[] = [];
    try {
      for (const asset of copy.assets) {
        const from = assetKey(id, asset.id);
        if (!(await this.blobs.has(from))) continue;
        const to = assetKey(newID, asset.id);
        await this.blobs.copy(from, to);
        copied.push(to);
      }
      await this.save(copy, { snapshotLabel: 'Duplicated' });
    } catch (cause) {
      for (const key of copied) await this.blobs.delete(key).catch(() => undefined);
      throw cause;
    }
    return copy;
  }

  async rename(id: UUID, name: string): Promise<void> {
    const project = await this.load(id);
    const trimmed = name.trim();
    project.name = trimmed === '' ? project.name : trimmed.slice(0, 80);
    project.modifiedAt = nowISODate();
    await this.save(project);
  }

  async setArchived(id: UUID, archived: boolean): Promise<void> {
    const project = await this.load(id);
    project.isArchived = archived;
    project.modifiedAt = nowISODate();
    await this.save(project);
  }

  /** Soft delete. Everything survives `TRASH_RETENTION_MS` and can be restored untouched. */
  async delete(id: UUID): Promise<void> {
    const project = await this.load(id);
    const now = nowISODate();
    project.deletedAt = now;
    project.modifiedAt = now;
    if (this.pending?.id === id) this.pending = null;
    await this.save(project);
  }

  async restore(id: UUID): Promise<void> {
    const project = await this.load(id);
    delete project.deletedAt;
    project.modifiedAt = nowISODate();
    await this.save(project);
  }

  /** Irreversible: the documents, the snapshots, the peaks, and every byte of audio. */
  async permanentlyDelete(id: UUID): Promise<void> {
    if (this.pending?.id === id) this.pending = null;
    await this.blobs.deleteMatching(`${id}.`);
    await this.blobs.deleteMatching(`render-${id}-`);

    const takes = await readStore('recordings', (store) => store.index('by-project').getAll(keyRange.only(id)));
    for (const take of takes) await this.blobs.delete(take.blobKey).catch(() => undefined);

    await transact(['projects', 'revisions', 'revisionDocuments', 'assets', 'peaks', 'recordings'], 'readwrite', async (tx) => {
      const revisions = await tx.store('revisions').index('by-project').getAll(keyRange.only(id));
      for (const revision of revisions) {
        await tx.store('revisionDocuments').delete(revision.key);
        await tx.store('revisions').delete(revision.key);
      }
      for (const asset of await tx.store('assets').index('by-project').getAll(keyRange.only(id))) {
        await tx.store('assets').delete(asset.key);
      }
      for (const peaks of await tx.store('peaks').index('by-project').getAll(keyRange.only(id))) {
        await tx.store('peaks').delete(peaks.key);
      }
      for (const take of await tx.store('recordings').index('by-project').getAll(keyRange.only(id))) {
        await tx.store('recordings').delete(take.id);
      }
      await tx.store('projects').delete(id);
    });

    if ((await readSetting<string | null>(LAST_OPENED_KEY, null)) === id) {
      await writeSetting(LAST_OPENED_KEY, null);
    }
    await this.refreshSummaries();
  }

  /** Reclaims projects whose recovery window has run out. Returns how many went. */
  async emptyExpiredTrash(): Promise<number> {
    const records = await readStore('projects', (store) => store.getAll());
    const cutoff = Date.now() - TRASH_RETENTION_MS;
    let removed = 0;
    for (const record of records) {
      if (!isDeleted(record.deletedAt)) continue;
      const deletedAt = Date.parse(record.deletedAt);
      if (Number.isFinite(deletedAt) && deletedAt < cutoff) {
        await this.permanentlyDelete(record.id);
        removed += 1;
      }
    }
    return removed;
  }

  // MARK: Version history

  async snapshots(projectID: UUID): Promise<ProjectSnapshot[]> {
    const revisions = await readStore('revisions', (store) => store.index('by-project').getAll(keyRange.only(projectID)));
    return revisions
      .filter((revision) => revision.isSnapshot)
      .sort((left, right) => right.sequence - left.sequence)
      .map((revision) => {
        const snapshot: ProjectSnapshot = {
          id: revision.key,
          createdAt: revision.createdAt,
          byteSize: revision.byteSize,
          isAutomatic: revision.isAutomatic,
        };
        if (revision.label !== undefined) snapshot.label = revision.label;
        return snapshot;
      });
  }

  /** An explicit, labelled version. Ignores the five-minute rule, which is the point. */
  async takeSnapshot(project: Project, label?: string): Promise<ProjectSnapshot> {
    await this.save(project, { snapshotLabel: label ?? 'Version' });
    const snapshots = await this.snapshots(project.id);
    const newest = snapshots[0];
    if (newest === undefined) throw StorageError.aborted();
    return newest;
  }

  /**
   * Restores an old version by saving it forward as the newest one, so the version the user
   * moved away from is still in the list. Nothing is ever overwritten in place.
   */
  async restoreSnapshot(snapshotID: string, projectID: UUID): Promise<Project> {
    if (!snapshotID.startsWith(`${projectID}:`)) {
      throw new StorageError(
        'notFound',
        'That version could not be identified.',
        'Pick a version from the list again. The project may have been reopened since.',
      );
    }
    const restored = await this.readRevision(snapshotID);
    if (restored === null) {
      throw new StorageError(
        'notFound',
        'That version is no longer on this device.',
        'Pick a more recent version, or import a project package you exported.',
      );
    }
    restored.id = projectID;
    restored.modifiedAt = nowISODate();
    delete restored.deletedAt;
    await this.save(restored, { snapshotLabel: 'Restored version' });
    return restored;
  }

  // MARK: Storage reporting

  async storageReport(): Promise<StorageReport> {
    const [projects, revisions, peaks, blobs, estimate] = await Promise.all([
      readStore('projects', (store) => store.getAll()),
      readStore('revisions', (store) => store.getAll()),
      readStore('peaks', (store) => store.getAll()),
      this.blobs.list(),
      storageEstimate(),
    ]);

    let projectsBytes = 0;
    for (const revision of revisions) projectsBytes += revision.byteSize;
    // Pointer records are tiny but not free, and the sum should add up to what the user sees.
    for (const record of projects) projectsBytes += record.name.length * 2 + 256;

    let peaksBytes = 0;
    for (const record of peaks) peaksBytes += record.byteSize;

    let mediaBytes = 0;
    let rendersBytes = 0;
    let recordingsBytes = 0;
    let downloadedPacksBytes = 0;
    for (const blob of blobs) {
      switch (blobCategory(blob.id)) {
        case 'media':
          mediaBytes += blob.byteSize;
          break;
        case 'render':
          rendersBytes += blob.byteSize;
          break;
        case 'take':
          recordingsBytes += blob.byteSize;
          break;
        case 'pack':
          downloadedPacksBytes += blob.byteSize;
          break;
      }
    }

    const totalBytes = projectsBytes + mediaBytes + peaksBytes + rendersBytes + recordingsBytes + downloadedPacksBytes;
    return {
      projectsBytes,
      mediaBytes,
      peaksBytes,
      rendersBytes,
      recordingsBytes,
      downloadedPacksBytes,
      availableBytes: estimate.quota > 0 ? Math.max(0, estimate.quota - estimate.usage) : 0,
      totalBytes,
      reclaimableBytes: peaksBytes + rendersBytes,
    };
  }

  /**
   * Deletes only what can be rebuilt from the originals: waveform caches and bounced
   * renders. Recordings, imports and projects are never touched — that is the whole promise
   * of the control.
   */
  async clearRegenerableCaches(): Promise<number> {
    let reclaimed = 0;

    const peaks = await readStore('peaks', (store) => store.getAll());
    for (const record of peaks) reclaimed += record.byteSize;
    await writeStore('peaks', (store) => store.clear());

    for (const blob of await this.blobs.list()) {
      if (blobCategory(blob.id) !== 'render') continue;
      reclaimed += blob.byteSize;
      await this.blobs.delete(blob.id);
    }

    reclaimed += await this.blobs.reconcile();
    log.info('cleared regenerable caches', { bytes: byteTag(reclaimed) });
    return reclaimed;
  }

  // MARK: Packages

  /** A file name that survives Files, AirDrop and Mail. */
  packageFileName(projectName: string): string {
    const cleaned = projectName
      .split(/[/\\:?%*|"<> ]/g)
      .join(' ')
      .trim();
    const base = cleaned === '' ? 'PadLab Project' : cleaned.slice(0, 60);
    return `${base}.${PACKAGE_EXTENSION}`;
  }

  /**
   * Builds the `.padlab` package. The result is a `Blob` assembled from the stored media
   * blobs rather than from their bytes, so exporting a 400 MB project never materialises
   * 400 MB: only the CRC pass walks the data, a megabyte at a time, yielding between chunks.
   */
  async exportPackage(
    projectID: UUID,
    options: {
      onProgress?: (fraction: number) => void;
      /**
       * Present for a sound pack: only these assets travel, and the arrangement is stripped.
       * Without it the "Sound Pack" scope emitted a byte-identical project backup under a
       * different filename — two scopes, one artefact.
       */
      assetIDs?: readonly UUID[];
    } = {},
  ): Promise<Blob> {
    await this.flush();
    const loaded = await this.load(projectID);
    const project =
      options.assetIDs === undefined ? withoutUnusedLibrary(loaded) : soundsOnlyProject(loaded, options.assetIDs);
    const text = encodeProject(project, { prettyPrinted: true });
    const modified = Date.parse(project.modifiedAt);

    const writer = new ZipWriter();
    await writer.addBytes(PROJECT_ENTRY_NAME, new TextEncoder().encode(text), new Date(Number.isFinite(modified) ? modified : Date.now()));

    // A stable order means two exports of an unchanged project produce the same archive.
    const assets = [...project.assets].sort((left, right) => (left.fileName < right.fileName ? -1 : 1));
    for (let index = 0; index < assets.length; index += 1) {
      const asset = assets[index] as SampleAsset;
      const blob = await this.blobs.tryGet(mediaKeyForAsset(projectID, asset));
      if (blob === undefined) continue;
      await writer.addBlob(`${MEDIA_ENTRY_PREFIX}${asset.fileName}`, blob, new Date(Date.parse(asset.createdAt) || Date.now()));
      options.onProgress?.((index + 1) / (assets.length + 1));
    }

    // Artwork travels with the project. Pads carry `artworkFileName` and it is the user's
    // own picture, so a backup that dropped it would come back with the board looking wrong.
    if (options.assetIDs === undefined) {
      const artworkIDs = new Set<string>();
      for (const bank of project.banks) {
        for (const pad of bank.pads) if (pad.artworkFileName !== undefined) artworkIDs.add(pad.artworkFileName);
      }
      for (const artworkID of artworkIDs) {
        const blob = await this.blobs.tryGet(artworkKey(projectID, artworkID));
        if (blob === undefined) continue;
        await writer.addBlob(`${ARTWORK_ENTRY_PREFIX}${artworkID}`, blob, new Date());
      }
    }

    options.onProgress?.(1);
    return writer.finish();
  }

  /**
   * Restores a package as a **new** project: importing the same backup twice gives two
   * independent projects. Asset ids are kept, so pads, patterns and scenes still point at
   * their sounds.
   */
  async importPackage(file: Blob, options: { onProgress?: (fraction: number) => void } = {}): Promise<Project> {
    const reader = await ZipReader.open(file);
    const projectEntry = reader.entries.find((entry) => entry.path === PROJECT_ENTRY_NAME);
    if (projectEntry === undefined) {
      throw new StorageError(
        'corrupt',
        'That file is not a PadLab backup. There is no project inside it.',
        'Choose a “.padlab” file exported from PadLab.',
      );
    }

    const text = new TextDecoder().decode(await reader.read(projectEntry, 64 * 1024 * 1024));
    let project: Project;
    try {
      project = decodeProject(text);
    } catch (cause) {
      if (isProjectSchemaError(cause)) throw cause;
      throw StorageError.corrupt('The project description inside that backup');
    }

    const projectID = newUUID();
    project.id = projectID;

    const byFileName = new Map<string, SampleAsset>();
    for (const asset of project.assets) byFileName.set(asset.fileName, asset);

    const written: string[] = [];
    const restored = new Set<string>();
    try {
      const mediaEntries = reader.entries.filter((entry) => entry.path.startsWith(MEDIA_ENTRY_PREFIX) && !entry.isDirectory);
      for (let index = 0; index < mediaEntries.length; index += 1) {
        const entry = mediaEntries[index] as ZipEntry;
        const fileName = entry.path.slice(MEDIA_ENTRY_PREFIX.length);
        const asset = byFileName.get(fileName);
        if (asset === undefined) continue;

        const key = assetKey(projectID, asset.id);
        await reader.extract(entry, key, this.blobs);
        written.push(key);
        restored.add(fileName);
        options.onProgress?.((index + 1) / (mediaEntries.length + 1));
      }

      const artworkEntries = reader.entries.filter(
        (entry) => entry.path.startsWith(ARTWORK_ENTRY_PREFIX) && !entry.isDirectory,
      );
      const restoredArtwork = new Set<string>();
      for (const entry of artworkEntries) {
        const artworkID = entry.path.slice(ARTWORK_ENTRY_PREFIX.length);
        if (artworkID === '') continue;
        const key = artworkKey(projectID, artworkID);
        await reader.extract(entry, key, this.blobs);
        written.push(key);
        restoredArtwork.add(artworkID);
      }
      // A pad pointing at a picture the archive did not carry loses the reference rather
      // than keeping a dangling one that renders as nothing for ever.
      for (const bank of project.banks) {
        for (const pad of bank.pads) {
          if (pad.artworkFileName !== undefined && !restoredArtwork.has(pad.artworkFileName)) {
            delete pad.artworkFileName;
          }
        }
      }

      // An asset with no file would leave a pad that can never make a sound. Dropping the
      // asset lets `sanitiseProject` clear the pads that referenced it while keeping their
      // names and colours, so the user can relink rather than rebuild.
      project.assets = project.assets.filter((asset) => restored.has(asset.fileName));
      // Everything that came out of the archive was extracted into *this* project's media
      // namespace, so it is this project's copy now — keeping the shared-library tag would
      // send later reads to a key the bytes are not under.
      for (const asset of project.assets) asset.tags = asset.tags.filter((entry) => entry !== LIBRARY_ASSET_TAG);
      delete project.artworkFileName;
      delete project.deletedAt;
      project.isArchived = false;
      project.modifiedAt = nowISODate();

      await this.save(project, { snapshotLabel: 'Imported' });
      options.onProgress?.(1);
      return project;
    } catch (cause) {
      for (const key of written) await this.blobs.delete(key).catch(() => undefined);
      throw cause;
    }
  }
}

// MARK: - Record helpers

function revisionKey(projectID: UUID, sequence: number): string {
  // Zero-padded so the string order matches the numeric order, which is what makes an index
  // scan of a project's revisions come back oldest-first without a sort.
  return `${projectID}:${String(sequence).padStart(9, '0')}`;
}

function toSummary(record: ProjectRecord): ProjectSummary {
  const summary: ProjectSummary = {
    id: record.id,
    name: record.name,
    modifiedAt: record.modifiedAt,
    createdAt: record.createdAt,
    padCount: record.padCount,
    assetCount: record.assetCount,
    tempo: record.tempo,
    byteSize: record.byteSize,
    isArchived: record.isArchived,
  };
  if (record.artworkFileName !== undefined) summary.artworkFileName = record.artworkFileName;
  if (isDeleted(record.deletedAt)) summary.deletedAt = record.deletedAt;
  return summary;
}

/**
 * Drops revisions that are neither current, nor the backup, nor snapshots, and trims the
 * snapshot list to the newest `SNAPSHOT_LIMIT`. Runs inside the save transaction so the cap
 * holds even when two saves land together.
 */
async function pruneRevisions(tx: Transaction, projectID: UUID, record: ProjectRecord): Promise<void> {
  const revisions: RevisionRecord[] = await tx.store('revisions').index('by-project').getAll(keyRange.only(projectID));
  const snapshots = revisions.filter((revision) => revision.isSnapshot).sort((left, right) => right.sequence - left.sequence);
  const keep = new Set<string>([record.currentRevision]);
  if (record.previousRevision !== undefined) keep.add(record.previousRevision);
  for (const snapshot of snapshots.slice(0, SNAPSHOT_LIMIT)) keep.add(snapshot.key);

  for (const revision of revisions) {
    if (keep.has(revision.key)) continue;
    await tx.store('revisionDocuments').delete(revision.key);
    await tx.store('revisions').delete(revision.key);
  }
}

/**
 * Keeps the asset index in step with the project document. The index is what lets an import
 * ask "do I already have these bytes?" without deserialising a project, and what the storage
 * screen counts.
 */
async function mirrorAssets(tx: Transaction, project: Project): Promise<void> {
  const store = tx.store('assets');
  const existing: AssetRecord[] = await store.index('by-project').getAll(keyRange.only(project.id));
  const previous = new Map<string, AssetRecord>();
  for (const record of existing) previous.set(record.key, record);

  const wanted = new Map<string, SampleAsset>();
  for (const asset of project.assets) wanted.set(assetKey(project.id, asset.id), asset);

  for (const record of existing) {
    if (!wanted.has(record.key)) await store.delete(record.key);
  }
  for (const [key, asset] of wanted) {
    const record: AssetRecord = {
      key,
      projectID: project.id,
      assetID: asset.id,
      name: asset.name,
      fileName: asset.fileName,
      contentHash: asset.contentHash,
      origin: asset.origin,
      createdAt: asset.createdAt,
      duration: asset.duration,
      sampleRate: asset.sampleRate,
      channelCount: asset.channelCount,
      frameCount: asset.frameCount,
      byteSize: asset.byteSize,
    };
    // A project's sounds barely change while its pads are being played with, so rewriting
    // every row on every autosave would be a hundred pointless writes a minute.
    if (matchesAssetRecord(previous.get(key), record)) continue;
    await store.put(record);
  }
}

function matchesAssetRecord(existing: AssetRecord | undefined, wanted: AssetRecord): boolean {
  return (
    existing !== undefined &&
    existing.name === wanted.name &&
    existing.fileName === wanted.fileName &&
    existing.contentHash === wanted.contentHash &&
    existing.origin === wanted.origin &&
    existing.createdAt === wanted.createdAt &&
    existing.duration === wanted.duration &&
    existing.sampleRate === wanted.sampleRate &&
    existing.channelCount === wanted.channelCount &&
    existing.frameCount === wanted.frameCount &&
    existing.byteSize === wanted.byteSize
  );
}

// MARK: - ZIP
//
// Store-only (uncompressed), for the same three reasons the native writer is: the reader and
// writer stay small enough to audit in one sitting, every entry round-trips byte-for-byte
// under a CRC-32 check, and the payload is already-compressed audio that deflate would buy a
// rounding error on. The result is a standard archive any unzip tool can open — and the
// iPhone app's `ProjectPackage` reads it directly.

const CRC_TABLE = ((): Uint32Array => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

/** Finalised in and out, so `update(update(0, a), b)` is the checksum of `a + b`. */
function crc32(previous: number, data: Uint8Array): number {
  let running = (previous ^ 0xffffffff) >>> 0;
  for (let index = 0; index < data.length; index += 1) {
    running = ((CRC_TABLE[(running ^ (data[index] as number)) & 0xff] as number) ^ (running >>> 8)) >>> 0;
  }
  return (running ^ 0xffffffff) >>> 0;
}

/** One megabyte per pass, with a yield after each, so a 400 MB export never blocks a frame. */
const CRC_CHUNK_BYTES = 1 << 20;

async function crc32OfBlob(blob: Blob): Promise<number> {
  let crc = 0;
  for (let offset = 0; offset < blob.size; offset += CRC_CHUNK_BYTES) {
    const slice = blob.slice(offset, Math.min(offset + CRC_CHUNK_BYTES, blob.size));
    crc = crc32(crc, new Uint8Array(await slice.arrayBuffer()));
  }
  return crc;
}

const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const VERSION_NEEDED = 20;
const METHOD_STORE = 0;
/** Bit 11 tells readers the file names are UTF-8, which they are. */
const UTF8_NAME_FLAG = 0x0800;
const ZIP_LIMIT = 0xffffffff;

interface CentralEntry {
  name: Uint8Array;
  crc: number;
  size: number;
  offset: number;
  dosTime: number;
  dosDate: number;
}

function dosTimestamp(date: Date): { time: number; date: number } {
  // MS-DOS cannot represent anything before 1980 or after 2107.
  const year = Math.min(Math.max(date.getUTCFullYear(), 1980), 2107);
  const month = Math.min(Math.max(date.getUTCMonth() + 1, 1), 12);
  const day = Math.min(Math.max(date.getUTCDate(), 1), 31);
  const hour = Math.min(Math.max(date.getUTCHours(), 0), 23);
  const minute = Math.min(Math.max(date.getUTCMinutes(), 0), 59);
  const second = Math.min(Math.max(date.getUTCSeconds(), 0), 59);
  return {
    time: (hour << 11) | (minute << 5) | (second >> 1),
    date: ((year - 1980) << 9) | (month << 5) | day,
  };
}

class ZipWriter {
  private readonly parts: BlobPart[] = [];
  private readonly entries: CentralEntry[] = [];
  private cursor = 0;

  async addBytes(path: string, data: Uint8Array, modified: Date): Promise<void> {
    // `Blob` only names the plain-`ArrayBuffer` case in its types; any typed array is a valid
    // part at runtime.
    await this.add(path, data as BlobPart, data.byteLength, crc32(0, data), modified);
  }

  async addBlob(path: string, blob: Blob, modified: Date): Promise<void> {
    await this.add(path, blob, blob.size, await crc32OfBlob(blob), modified);
  }

  private async add(path: string, payload: BlobPart, size: number, crc: number, modified: Date): Promise<void> {
    const name = new TextEncoder().encode(path);
    // Classic ZIP addresses everything with 32-bit offsets. Rather than implement ZIP64 for a
    // case a phone project will never reach, refuse early with a fixable message.
    if (this.cursor + size + name.byteLength + 30 > ZIP_LIMIT) {
      throw new StorageError(
        'quotaExceeded',
        `“${path}” pushes this backup past 4 GB, which a single backup file cannot hold.`,
        'Bounce or remove some sounds and export again.',
      );
    }

    const stamp = dosTimestamp(modified);
    const header = new Uint8Array(30 + name.byteLength);
    const view = new DataView(header.buffer);
    view.setUint32(0, LOCAL_HEADER_SIGNATURE, true);
    view.setUint16(4, VERSION_NEEDED, true);
    view.setUint16(6, UTF8_NAME_FLAG, true);
    view.setUint16(8, METHOD_STORE, true);
    view.setUint16(10, stamp.time, true);
    view.setUint16(12, stamp.date, true);
    // Sizes are known before anything is written here, so no data descriptor is needed —
    // some minimal unzip implementations skip those.
    view.setUint32(14, crc, true);
    view.setUint32(18, size, true);
    view.setUint32(22, size, true);
    view.setUint16(26, name.byteLength, true);
    view.setUint16(28, 0, true);
    header.set(name, 30);

    this.parts.push(header);
    this.parts.push(payload);
    this.entries.push({ name, crc, size, offset: this.cursor, dosTime: stamp.time, dosDate: stamp.date });
    this.cursor += header.byteLength + size;
  }

  finish(): Blob {
    const directoryOffset = this.cursor;
    let directorySize = 0;
    for (const entry of this.entries) directorySize += 46 + entry.name.byteLength;

    const directory = new Uint8Array(directorySize + 22);
    const view = new DataView(directory.buffer);
    let cursor = 0;
    for (const entry of this.entries) {
      view.setUint32(cursor, CENTRAL_HEADER_SIGNATURE, true);
      view.setUint16(cursor + 4, VERSION_NEEDED, true);
      view.setUint16(cursor + 6, VERSION_NEEDED, true);
      view.setUint16(cursor + 8, UTF8_NAME_FLAG, true);
      view.setUint16(cursor + 10, METHOD_STORE, true);
      view.setUint16(cursor + 12, entry.dosTime, true);
      view.setUint16(cursor + 14, entry.dosDate, true);
      view.setUint32(cursor + 16, entry.crc, true);
      view.setUint32(cursor + 20, entry.size, true);
      view.setUint32(cursor + 24, entry.size, true);
      view.setUint16(cursor + 28, entry.name.byteLength, true);
      view.setUint16(cursor + 30, 0, true);
      view.setUint16(cursor + 32, 0, true);
      view.setUint16(cursor + 34, 0, true);
      view.setUint16(cursor + 36, 0, true);
      view.setUint32(cursor + 38, 0, true);
      view.setUint32(cursor + 42, entry.offset, true);
      directory.set(entry.name, cursor + 46);
      cursor += 46 + entry.name.byteLength;
    }

    view.setUint32(cursor, END_OF_CENTRAL_DIRECTORY_SIGNATURE, true);
    view.setUint16(cursor + 4, 0, true);
    view.setUint16(cursor + 6, 0, true);
    view.setUint16(cursor + 8, this.entries.length, true);
    view.setUint16(cursor + 10, this.entries.length, true);
    view.setUint32(cursor + 12, directorySize, true);
    view.setUint32(cursor + 16, directoryOffset, true);
    view.setUint16(cursor + 20, 0, true);

    this.parts.push(directory);
    return new Blob(this.parts, { type: 'application/zip' });
  }
}

interface ZipEntry {
  path: string;
  crc: number;
  size: number;
  localHeaderOffset: number;
  isDirectory: boolean;
}

class ZipReader {
  readonly entries: readonly ZipEntry[];
  private readonly file: Blob;

  private constructor(file: Blob, entries: ZipEntry[]) {
    this.file = file;
    this.entries = entries;
  }

  /** The end-of-directory record sits in the last 22 bytes plus up to 64 KB of comment. */
  private static readonly MAXIMUM_TAIL_SCAN = 66 * 1024;

  static async open(file: Blob): Promise<ZipReader> {
    if (file.size < 22) {
      throw new StorageError(
        'corrupt',
        'That file is empty or too small to be a PadLab backup.',
        'Choose a “.padlab” file exported from PadLab.',
      );
    }

    const tailLength = Math.min(file.size, ZipReader.MAXIMUM_TAIL_SCAN);
    const tail = new Uint8Array(await file.slice(file.size - tailLength).arrayBuffer());
    let endIndex = -1;
    for (let index = tail.length - 22; index >= 0; index -= 1) {
      if (tail[index] === 0x50 && tail[index + 1] === 0x4b && tail[index + 2] === 0x05 && tail[index + 3] === 0x06) {
        endIndex = index;
        break;
      }
    }
    if (endIndex < 0) {
      throw new StorageError(
        'corrupt',
        'That file is not a PadLab backup. Its index is missing.',
        'Choose a “.padlab” file exported from PadLab.',
      );
    }

    const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
    const entryCount = tailView.getUint16(endIndex + 10, true);
    const directorySize = tailView.getUint32(endIndex + 12, true);
    const directoryOffset = tailView.getUint32(endIndex + 16, true);
    if (directoryOffset + directorySize > file.size) {
      throw StorageError.corrupt('The index inside that backup');
    }

    const directory = new Uint8Array(await file.slice(directoryOffset, directoryOffset + directorySize).arrayBuffer());
    const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);
    const entries: ZipEntry[] = [];
    let cursor = 0;
    while (cursor + 46 <= directory.length && entries.length < entryCount) {
      if (view.getUint32(cursor, true) !== CENTRAL_HEADER_SIGNATURE) break;
      const method = view.getUint16(cursor + 10, true);
      const crc = view.getUint32(cursor + 16, true);
      const compressedSize = view.getUint32(cursor + 20, true);
      const uncompressedSize = view.getUint32(cursor + 24, true);
      const nameLength = view.getUint16(cursor + 28, true);
      const extraLength = view.getUint16(cursor + 30, true);
      const commentLength = view.getUint16(cursor + 32, true);
      const localHeaderOffset = view.getUint32(cursor + 42, true);

      const nameEnd = cursor + 46 + nameLength;
      if (nameEnd > directory.length) throw StorageError.corrupt('A file name inside that backup');
      const rawPath = new TextDecoder().decode(directory.subarray(cursor + 46, nameEnd));

      if (method !== METHOD_STORE) {
        throw new StorageError(
          'corrupt',
          `“${rawPath}” inside this archive is compressed.`,
          'PadLab backups store their files uncompressed. Re-export the project from PadLab.',
        );
      }
      if (compressedSize !== uncompressedSize) {
        throw StorageError.corrupt(`The sizes recorded for “${rawPath}”`);
      }

      const safePath = safeRelativePath(rawPath);
      if (safePath === null) {
        // A path that climbs out of the folder is the classic archive attack; refuse the
        // whole file rather than quietly importing part of it.
        throw new StorageError(
          'corrupt',
          'This backup contains a file path that points outside the project folder, so nothing was imported.',
          'Only open “.padlab” files exported from PadLab.',
        );
      }

      entries.push({
        path: safePath,
        crc,
        size: uncompressedSize,
        localHeaderOffset,
        isDirectory: rawPath.endsWith('/'),
      });
      cursor = nameEnd + extraLength + commentLength;
    }

    if (entries.length === 0) {
      throw new StorageError('corrupt', 'That backup has no files inside it.', 'Export the project again from PadLab.');
    }
    return new ZipReader(file, entries);
  }

  private async dataOffset(entry: ZipEntry): Promise<number> {
    const header = new Uint8Array(await this.file.slice(entry.localHeaderOffset, entry.localHeaderOffset + 30).arrayBuffer());
    if (header.byteLength < 30) throw StorageError.corrupt('That backup');
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
    if (view.getUint32(0, true) !== LOCAL_HEADER_SIGNATURE) {
      throw StorageError.corrupt('The index inside that backup');
    }
    return entry.localHeaderOffset + 30 + view.getUint16(26, true) + view.getUint16(28, true);
  }

  /** Reads a whole entry into memory. Only used for `project.json`, which is bounded. */
  async read(entry: ZipEntry, limit: number): Promise<Uint8Array> {
    if (entry.size > limit) throw StorageError.corrupt('An entry inside that backup');
    const start = await this.dataOffset(entry);
    const data = new Uint8Array(await this.file.slice(start, start + entry.size).arrayBuffer());
    if (crc32(0, data) !== entry.crc) {
      throw StorageError.corrupt(`“${entry.path}” inside that backup`);
    }
    return data;
  }

  /**
   * Streams an entry into the blob store, checking its CRC as it goes. Nothing is committed
   * to the index until the checksum matches, so a damaged package cannot leave a project
   * holding audio that is quietly wrong.
   */
  async extract(entry: ZipEntry, blobKey: string, blobs: BlobStore): Promise<void> {
    const start = await this.dataOffset(entry);
    const writer = await blobs.createWriter(blobKey, { mimeType: 'application/octet-stream' });
    let crc = 0;
    try {
      for (let offset = 0; offset < entry.size; offset += CRC_CHUNK_BYTES) {
        const end = Math.min(offset + CRC_CHUNK_BYTES, entry.size);
        const chunk = new Uint8Array(await this.file.slice(start + offset, start + end).arrayBuffer());
        if (chunk.byteLength === 0) throw StorageError.corrupt(`“${entry.path}” inside that backup`);
        crc = crc32(crc, chunk);
        await writer.write(chunk);
      }
      if (crc !== entry.crc) {
        throw new StorageError(
          'corrupt',
          `“${entry.path}” inside that backup failed its checksum, so the sound file is damaged.`,
          'Ask for the backup again, or export a fresh one from the device that made it.',
        );
      }
      await writer.close();
    } catch (cause) {
      await writer.abort().catch(() => undefined);
      throw cause;
    }
  }
}

/** `null` for anything absolute, escaping upwards, or otherwise not a plain relative path. */
function safeRelativePath(raw: string): string | null {
  if (raw === '' || raw.length >= 1024 || raw.startsWith('/') || raw.includes('\\') || raw.includes('\0')) return null;
  const components = raw.split('/').filter((component) => component !== '');
  if (components.length === 0) return null;
  for (const component of components) {
    if (component === '.' || component === '..') return null;
  }
  return components.join('/');
}
